import * as THREE from "three";
import { inked, toon } from "../toon";
import type { SkaterLook } from "./look";
import { cloth } from "./materials";
import { facets } from "./geom";

// The backpack (#136) on the rider's back and the paintball gun it holds,
// both in torso space (up +y, chest toward -x, arms at ±z), in the active
// style's materials. The pack is always on; the gun shows only while it's
// the item out (`SkaterModel` holds it in both hands).

/** The pack in the deck's colour, so it goes with the board: a body, a top flap and two shoulder straps. */
export function buildBackpack(look: SkaterLook): THREE.Group {
  const pack = new THREE.Group();
  pack.name = "backpack";
  const body = cloth(look.deck);
  const strap = cloth("#2a2a2a");
  const box = new THREE.BoxGeometry(0.12, 0.3, 0.26, 1, 1, 1);
  pack.add(inked(box, body, 0.012));
  const flapGeo = new THREE.CapsuleGeometry(0.065, 0.16, facets(4, 1), facets(10));
  flapGeo.rotateX(Math.PI / 2);
  const flap = inked(flapGeo, body, 0.012);
  flap.position.set(0, 0.15, 0);
  pack.add(flap);
  for (const z of [-0.08, 0.08]) {
    const s = inked(new THREE.BoxGeometry(0.03, 0.26, 0.035), strap, 0.008);
    s.position.set(-0.075, 0.02, z);
    pack.add(s);
  }
  pack.position.set(0.19, 0.27, 0);
  return pack;
}

/** Where the hands go on the gun, in its own space (barrel along -x). */
export const GUN_GRIP = new THREE.Vector3(0.05, -0.08, 0);
export const GUN_FOREGRIP = new THREE.Vector3(-0.17, -0.04, 0);
/**
 * The gun's place in torso space, shouldered (#143): the stock's end against
 * the front of the back arm's shoulder, the body at shoulder height so the
 * hopper (the sights) comes up by the eyes, the barrel straight ahead.
 */
export const GUN_AT = new THREE.Vector3(-0.3, 0.6, -0.1);

/** A chunky toy paintball marker: a body, a barrel, a hopper of balls on top, a grip. Hidden until it's the item out. */
export function buildGun(): THREE.Group {
  const gun = new THREE.Group();
  gun.name = "paintball-gun";
  const body = toon("#3c3f8f", { roughness: 0.45 }, "plastic");
  const dark = toon("#1e1e24", { roughness: 0.6 }, "plastic");
  const hopper = toon("#ffd23f", { roughness: 0.35 }, "plastic");
  gun.add(inked(new THREE.BoxGeometry(0.3, 0.075, 0.06), body, 0.01));
  const barrelGeo = new THREE.CylinderGeometry(0.02, 0.022, 0.26, facets(12));
  barrelGeo.rotateZ(Math.PI / 2);
  const barrel = inked(barrelGeo, dark, 0.008);
  barrel.position.set(-0.27, 0.01, 0);
  gun.add(barrel);
  const hop = inked(new THREE.SphereGeometry(0.065, facets(14), facets(10)), hopper, 0.01);
  hop.scale.set(1.3, 0.9, 0.9);
  hop.position.set(0.02, 0.09, 0);
  gun.add(hop);
  const grip = inked(new THREE.BoxGeometry(0.045, 0.1, 0.045), dark, 0.008);
  grip.position.set(GUN_GRIP.x, GUN_GRIP.y + 0.02, 0);
  grip.rotation.z = -0.25;
  gun.add(grip);
  const fore = inked(new THREE.BoxGeometry(0.04, 0.07, 0.04), dark, 0.008);
  fore.position.set(GUN_FOREGRIP.x, GUN_FOREGRIP.y + 0.01, 0);
  gun.add(fore);
  gun.position.copy(GUN_AT);
  gun.visible = false;
  return gun;
}

/** The washer's wand tips down this far (rad) from the gun's level, along its jet (`WASH_PITCH`). */
export const WAND_TIP = 0.28;
/** The wand's nozzle in its own space, where the drawn jet starts. */
export const WAND_NOZZLE = new THREE.Vector3(-0.86, -0.02, 0);

/**
 * The power washer (#151): a trigger gun with a long lance, held like the
 * paintball gun (same grip and foregrip spots, `GUN_AT`), and a little
 * tank strapped under the pack. Both hidden until it's the item out.
 */
export function buildWasher(): { wand: THREE.Group; tank: THREE.Group } {
  const wand = new THREE.Group();
  wand.name = "washer-wand";
  const body = toon("#f2c230", { roughness: 0.45 }, "plastic");
  const dark = toon("#1e1e24", { roughness: 0.6 }, "plastic");
  const steel = toon("#b9c0c8", { roughness: 0.3, metalness: 0.6 }, "metal");
  wand.add(inked(new THREE.BoxGeometry(0.2, 0.07, 0.055), body, 0.01));
  const lanceGeo = new THREE.CylinderGeometry(0.012, 0.012, 0.66, facets(10));
  lanceGeo.rotateZ(Math.PI / 2);
  const lance = inked(lanceGeo, steel, 0.008);
  lance.position.set(-0.43, WAND_NOZZLE.y, 0);
  wand.add(lance);
  const nozzleGeo = new THREE.CylinderGeometry(0.018, 0.026, 0.07, facets(10));
  nozzleGeo.rotateZ(Math.PI / 2);
  const nozzle = inked(nozzleGeo, dark, 0.008);
  nozzle.position.set(WAND_NOZZLE.x + 0.035, WAND_NOZZLE.y, 0);
  wand.add(nozzle);
  const grip = inked(new THREE.BoxGeometry(0.045, 0.1, 0.045), dark, 0.008);
  grip.position.set(GUN_GRIP.x, GUN_GRIP.y + 0.02, 0);
  grip.rotation.z = -0.25;
  wand.add(grip);
  // A foam sleeve on the lance where the front hand holds it.
  const sleeveGeo = new THREE.CylinderGeometry(0.022, 0.022, 0.12, facets(10));
  sleeveGeo.rotateZ(Math.PI / 2);
  const sleeve = inked(sleeveGeo, dark, 0.008);
  sleeve.position.set(GUN_FOREGRIP.x, GUN_FOREGRIP.y + 0.02, 0);
  wand.add(sleeve);
  wand.position.copy(GUN_AT);
  wand.rotation.z = WAND_TIP;
  wand.visible = false;

  const tank = new THREE.Group();
  tank.name = "washer-tank";
  const tankGeo = new THREE.CylinderGeometry(0.075, 0.075, 0.26, facets(14));
  tankGeo.rotateX(Math.PI / 2);
  tank.add(inked(tankGeo, body, 0.01));
  const capGeo = new THREE.CylinderGeometry(0.03, 0.03, 0.03, facets(10));
  const cap = inked(capGeo, dark, 0.008);
  cap.position.set(0.03, 0.075, 0.06);
  tank.add(cap);
  // Under the pack, on the back (the pack sits at 0.19, 0.27).
  tank.position.set(0.2, 0.05, 0);
  tank.visible = false;
  return { wand, tank };
}
